import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { TWModal } from '../../../components/ui';
import { cn } from '../../../components/ui/_cn';
import connectApi from '../../../services/connectApi';
import { apiMessage } from '../connect-api-message';
import FinButton from './fin-button';
import FinDropdown from './fin-dropdown';
import COPY from './connect-finance-copy';
import {
  CADENCES, MONTH_DAYS, WEEKDAYS, bodyOf, changedFrom, formOf,
} from './payout-schedule-model';

// Spec 2026-10-20 §2.2. The restaurant's own schedule editor, opened from
// Overview's "Edit" beside the Schedule row (owner, 2026-10-20: daily
// payouts cost nothing, so restaurants edit their schedule again). Ours and
// not Stripe's, because Stripe's "Payout information" dialog cannot be
// opened from outside its component — it lives behind the "Update" link
// inside the balances card the Payouts page mounts — and because Stripe's
// editor on these accounts is recorded (2026-09-22) to ask the restaurant
// for a Stripe sign-in before a two-field form. The schedule is written
// through PUT Connect/PayoutSchedule, which answers the refreshed balance;
// the bank is not edited here — the account strip under the hero opens
// Stripe's account management for that.
//
// Delay days are never edited: Stripe fixes them per country, and the
// hero's rail keeps reading `scheduleDelayDays` to say when money arrives.
const AUTOMATIC = CADENCES.map((c) => c.value);

// The two radio boxes: the chosen one takes the blue border in place of
// the plain one — swapped, never stacked.
const boxClass = (selected) =>
  cn('rounded-lg border px-4 py-3.5', selected ? 'border-fin-blue' : 'border-fin-border');

const PayoutScheduleModal = ({ open, balance, companyId, onClose, onSaved }) => {
  const copy = COPY.restaurant;
  // Memoised on the schedule fields, not the balance object: Overview
  // installs a new object on every re-read (Retry, an instant payout), and
  // one that changed nothing about the schedule must not wipe a choice
  // mid-edit.
  const interval = balance?.scheduleInterval;
  const weeklyAnchor = balance?.scheduleWeeklyAnchor;
  const monthlyAnchor = balance?.scheduleMonthlyAnchor;
  const initial = useMemo(
    () => formOf({ scheduleInterval: interval, scheduleWeeklyAnchor: weeklyAnchor, scheduleMonthlyAnchor: monthlyAnchor }),
    [interval, weeklyAnchor, monthlyAnchor],
  );
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // A reopen must show what the account has now, not what the reader left
  // in the form last time — and a schedule that changed while the modal is
  // open (a refresh finishing) has to reach the fields.
  useEffect(() => {
    if (open) {
      setForm(initial);
      setError('');
    }
  }, [open, initial]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const manual = form.interval === 'manual';
  const automatic = AUTOMATIC.includes(form.interval);
  const changed = changedFrom(form, balance);
  // The delay is Stripe's, per country; the line says what it is, never
  // edits it.
  const dailyHelp = copy.scheduleDailyHelp(balance?.scheduleDelayDays);

  // Automatic, chosen from Manual or from nothing: back to the account's own
  // cadence when it has one (so Manual-and-back leaves nothing to save),
  // else every day.
  const chooseAutomatic = () => {
    if (!automatic) set({ interval: AUTOMATIC.includes(initial.interval) ? initial.interval : 'daily' });
  };

  const save = async () => {
    if (saving || !changed) return;
    setSaving(true);
    setError('');
    try {
      const next = connectApi.toConnectBalance(
        await connectApi.updatePayoutSchedule(companyId, bodyOf(form)),
      );
      message.success(copy.payoutScheduleSaved);
      // A null balance means the schedule was saved but the API could not
      // re-read the balance afterwards (it answers 204, an empty body,
      // rather than reporting a successful write as a failure); the caller
      // re-reads on its own in that case.
      if (onSaved) onSaved(next || null);
      onClose();
    } catch (caught) {
      // The server's own words, sanitised (no dashboard.stripe.com link, no
      // stack trace) — apiMessage is the one place a server message is read.
      setError(apiMessage(caught) || copy.payoutScheduleFailed);
    } finally {
      setSaving(false);
    }
  };

  // A save in flight cannot be abandoned: the X, Escape and the backdrop go
  // through TWModal's onClose, Cancel is disabled below — a refusal must be
  // seen, and a success must close exactly once.
  const close = saving ? () => {} : onClose;

  return (
    <TWModal
      open={open}
      onClose={close}
      title={copy.payoutScheduleTitle}
      description={copy.payoutScheduleSub}
      size="md"
      showCloseButton
      // The panel's own overflow-hidden would clip FinDropdown's menus (the
      // weekday and 31-day lists run past the modal's bottom edge); with
      // it visible, the body rounds its own bottom corners to the panel's.
      panelClassName="overflow-visible"
      bodyClassName="bg-fin-surface px-5 py-5 sm:rounded-b-md"
    >
      <div className="space-y-2.5">
        <div className={boxClass(manual)}>
          <label className="flex cursor-pointer items-start gap-2.5 text-[13.5px] font-semibold text-fin-ink">
            <input
              type="radio"
              name="payout-schedule"
              checked={manual}
              onChange={() => set({ interval: 'manual' })}
              className="mt-[3px] h-3.5 w-3.5 flex-none accent-fin-blue"
            />
            {/* The helper sits beside "Manual" when there is room and drops
                under it on a narrow screen. */}
            <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
              Manual{' '}
              <span className="font-medium text-fin-muted">{copy.scheduleManualHelp}</span>
            </span>
          </label>
        </div>

        <div className={boxClass(automatic)}>
          <label className="flex cursor-pointer items-center gap-2.5 text-[13.5px] font-semibold text-fin-ink">
            <input
              type="radio"
              name="payout-schedule"
              checked={automatic}
              onChange={chooseAutomatic}
              className="h-3.5 w-3.5 accent-fin-blue"
            />
            Automatic every
          </label>
          {automatic ? (
            // pl-6: the radio's 14px and the label's 10px gap, so the
            // dropdowns line up under the label's text.
            <div className="mt-3 flex flex-wrap items-center gap-2 pl-6">
              <FinDropdown
                mode="radio"
                size="md"
                aria-label="Interval"
                value={form.interval}
                options={CADENCES}
                onChange={(next) => set({ interval: next })}
              />
              {/* The connector and its dropdown wrap together, so "on" never
                  ends a line on its own. */}
              {form.interval === 'weekly' ? (
                <span className="inline-flex items-center gap-2">
                  <span className="text-[13.5px] text-fin-muted">on</span>
                  <FinDropdown
                    mode="radio"
                    size="md"
                    aria-label="Day of week"
                    value={form.weeklyAnchor}
                    options={WEEKDAYS}
                    placeholder={copy.scheduleWeekdayPlaceholder}
                    onChange={(next) => set({ weeklyAnchor: next })}
                  />
                </span>
              ) : null}
              {form.interval === 'monthly' ? (
                <span className="inline-flex items-center gap-2">
                  <span className="text-[13.5px] text-fin-muted">on the</span>
                  <FinDropdown
                    mode="radio"
                    size="md"
                    aria-label="Day of month"
                    value={form.monthlyAnchor}
                    options={MONTH_DAYS}
                    onChange={(next) => set({ monthlyAnchor: next })}
                  />
                </span>
              ) : null}
            </div>
          ) : null}
          {form.interval === 'daily' && dailyHelp ? (
            <p className="mt-2 pl-6 text-[13px] font-medium text-fin-muted">{dailyHelp}</p>
          ) : null}
        </div>
      </div>

      {error ? (
        <p role="alert" className="mt-4 text-[13px] text-fin-red">
          {error}
        </p>
      ) : null}

      <div className="mt-5 flex justify-end gap-2">
        <FinButton disabled={saving} onClick={onClose}>Cancel</FinButton>
        <FinButton variant="primary" disabled={!changed || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </FinButton>
      </div>
    </TWModal>
  );
};

export default PayoutScheduleModal;
